import { useState } from 'react';
import { useLocation } from 'react-router';
import svgPaths from "../../imports/svg-0oqr7j0o96";
import { PrimaryButton } from './PrimaryButton';
import {
  customerApiErrorMessage,
  resendVerification,
} from '../../lib/customer-api';
import { getCustomer } from '../../lib/customer-auth';
import { toast } from '../../lib/toast';

export function SignUpSuccess() {
  const location = useLocation();
  // Pull email + dev-only verification URL from the nav state set by
  // step 2. Fall back to the locally cached user for the email so a
  // refresh still works.
  const state = location.state as {
    email?: string;
    verificationUrl?: string;
  } | null;
  const email = state?.email || getCustomer()?.email || '';
  const devUrl = state?.verificationUrl;

  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [latestDevUrl, setLatestDevUrl] = useState<string | undefined>(devUrl);

  const handleResend = async () => {
    if (!email) {
      setResendError(
        'We need your email to resend the verification — try signing up again.',
      );
      return;
    }
    setResending(true);
    setResendMessage(null);
    setResendError(null);
    try {
      const res = await resendVerification(email);
      // Guard against a leaked i18n key (e.g. "messages.email..." when the
      // backend's i18n didn't resolve) — never toast a raw key at the user.
      const message =
        res.message && !res.message.startsWith('messages.')
          ? res.message
          : 'If that email is on file and not yet verified, a new verification link is on its way.';
      setResendMessage(message);
      toast.success(message);
      if (res.verification_url) setLatestDevUrl(res.verification_url);
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not resend right now.');
      setResendError(msg);
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full max-w-[420px] flex flex-col items-center text-center">
      <div className="relative w-[181px] h-[125px] mb-8">
        <svg
          className="absolute inset-0 w-full h-[124px]"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 181 124"
        >
          <g>
            <path d={svgPaths.pab57f00} fill="white" />
            <path d={svgPaths.pab57f00} fill="#3A3335" fillOpacity="0.04" />
          </g>
        </svg>
        <div className="absolute left-[29.28%] right-[30.39%] top-[41.6%] bottom-0">
          <svg
            className="w-full h-full"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 73 73"
          >
            <circle cx="36.5" cy="36.5" fill="#C6D8D3" r="36.5" />
          </svg>
        </div>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-[12.5px] material-icons text-[28px] text-white">
          {``}
        </span>
      </div>

      <h1 className="font-['Playfair_Display',sans-serif] font-extrabold leading-[48px] text-[#8e2a2f] text-[34px] tracking-[-0.0944px] mb-2">
        Thank you!
      </h1>

      <div
        className="font-['DM_Sans',sans-serif] font-normal text-[#908e8e] text-[14px] tracking-[-0.1px] mb-8"
        style={{ fontVariationSettings: "'opsz' 14" }}
      >
        <p className="leading-[21px]">
          We sent a verification email to{' '}
          <span className="text-[#3a3335] font-bold">
            {email || 'your inbox'}
          </span>
          .
        </p>
        <p className="leading-[21px]">
          Click the button in that email to verify your account.
        </p>
      </div>

      <div className="w-full flex flex-col gap-3">
        <PrimaryButton
          onClick={handleResend}
          className="w-full"
          disabled={resending}
        >
          {resending ? 'Sending…' : "Didn't get it? Resend email"}
        </PrimaryButton>

        {resendMessage ? (
          <p className="text-[#3a3335] text-[13px]">{resendMessage}</p>
        ) : null}
        {resendError ? (
          <p className="text-[#8e2a2f] text-[13px]">{resendError}</p>
        ) : null}

        {/* Dev-only — the backend returns the verification URL in the
            response while SMTP isn't wired up, so QA can click it
            directly. Disappears in production. */}
        {latestDevUrl ? (
          <div className="mt-2 rounded-md bg-[#fafaf7] border border-[#ede8e9] p-3 text-left">
            <p className="text-[11px] text-[#908e8e] mb-1">
              Dev shortcut (no SMTP yet)
            </p>
            <a
              href={latestDevUrl}
              className="break-all text-[12px] text-[#8e2a2f] font-bold hover:underline"
            >
              {latestDevUrl}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
